import { UTCDate } from '@date-fns/utc';
import { addDays, isBefore, isWithinInterval } from 'date-fns';
import { round } from 'lodash';
import { InvoiceEvidenceView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { MetricCardVariant } from '../components/empty-state/invoices-metric-card';
import { getInReviewInvoices } from './get-in-review-invoices';
import { hasValidDueDate } from './has-valid-due-date';

const today = new UTCDate();
const inSevenDays = addDays(today, 7);

type InvoiceMetric = {
  variant: MetricCardVariant;
  metric: number;
  normalizedAmount: number;
  currency: SupportedCountryCurrency;
};

type GetInvoiceMetricsArgs = {
  evidences: InvoiceEvidenceView[];
  baseCurrency: SupportedCountryCurrency;
};

export const getInvoiceMetrics = ({ evidences, baseCurrency }: GetInvoiceMetricsArgs): InvoiceMetric[] => {
  const outstandingEvidences = evidences.filter((e) => !e.isPaid);
  const outstandingEvidencesWithAmount = outstandingEvidences.filter(hasAmount);
  const outstandingEvidencesAmount = getTotalBaseCurrencyAmount(outstandingEvidencesWithAmount);
  const outstandingEvidencesWithValidDueDate = outstandingEvidencesWithAmount.filter(hasValidDueDate);

  const overdueEvidences = outstandingEvidencesWithValidDueDate.filter((e) => isBefore(e.billingDates.dueDate, today));
  const overdueEvidencesAmount = getTotalBaseCurrencyAmount(overdueEvidences);

  const dueInSevenDaysEvidences = outstandingEvidencesWithValidDueDate.filter((e) =>
    isWithinInterval(e.billingDates.dueDate, { start: today, end: inSevenDays }),
  );
  const dueInSevenDaysEvidencesAmount = getTotalBaseCurrencyAmount(dueInSevenDaysEvidences);

  const inReviewEvidences = getInReviewInvoices(evidences);
  const inReviewEvidencesAmount = getTotalBaseCurrencyAmount(inReviewEvidences);

  return [
    {
      variant: 'OUTSTANDING',
      metric: outstandingEvidences.length,
      normalizedAmount: outstandingEvidencesAmount,
      currency: baseCurrency,
    },
    {
      variant: 'OVERDUE',
      metric: overdueEvidences.length,
      normalizedAmount: overdueEvidencesAmount,
      currency: baseCurrency,
    },
    {
      variant: 'DUE_7_DAYS',
      metric: dueInSevenDaysEvidences.length,
      normalizedAmount: dueInSevenDaysEvidencesAmount,
      currency: baseCurrency,
    },
    {
      variant: 'REVIEW_MATCHES',
      metric: inReviewEvidences.length,
      normalizedAmount: inReviewEvidencesAmount,
      currency: baseCurrency,
    },
  ];
};

const hasAmount = (
  evidence: InvoiceEvidenceView,
): evidence is InvoiceEvidenceView & {
  baseCurrency: SupportedCountryCurrency;
  baseCurrencyAmount: number;
} => {
  return !!evidence.baseCurrency && !!evidence.baseCurrencyAmount;
};

const getTotalBaseCurrencyAmount = (evidences: InvoiceEvidenceView[]) => {
  const total = evidences.reduce((acc, evidence) => acc + evidence.baseCurrencyAmount, 0);

  return round(total, 2);
};
